import { RootState } from '@/types/graph';
import { addToTotalQueue } from '@/reducers/graph';
import { Scenario, ScenarioEntity } from '@/types/scenario';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { scenarioClient } from '@/util/scenarioClient';
import { formatRawValue } from '@/util/questionnaire-computations';
import { useActiveQueue } from '../useActiveQueue';

export const useSaveScenario = (buildPreview?: boolean) => {
  const [scenarioTxt, setScenarioTxt] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedScenario, setSavedScenario] = useState<ScenarioEntity | null>(
    null,
  );

  const [orderedResponses, setOrderedResponses] = useState<string[][]>([]);
  const [doSave, setSave] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );
  const questionnaire = useSelector((state: RootState) => state.questionnaire);
  const userResponses = useSelector((state: RootState) => state.userResponses);
  const consultation = useSelector((state: RootState) => state.consultation);
  const questions = useSelector((state: RootState) => state.kb.questions);
  const responses = useSelector((state: RootState) => state.kb.responses);
  const activeQueue = useActiveQueue();

  const sectionsByPage = useSelector((state: RootState) =>
    state.questionnaire.config ? state.questionnaire.config.pages : undefined,
  );

  // Generate detailed description with patient info and Q&A
  const generateScenarioDescription = useCallback((): string => {
    const parts: string[] = ['', '---'];

    // Add patient information
    if (consultation.patient) {
      const patient = consultation.patient;
      parts.push(
        `${patient.firstName} ${patient.lastName} ${
          patient.dateOfBirth
            ? new Date(patient.dateOfBirth).toLocaleDateString('fr-FR')
            : ''
        } ${patient.sex}`,
      );
    }

    orderedResponses.forEach((page) => {
      if (page.length > 0) {
        page
          .filter(
            (qId) => !['CONSULTATION_DATE', 'CONSULTATION_TIME'].includes(qId),
          )
          .forEach((qId) => {
            const question = questions[qId];
            if (question && userResponses[qId]) {
              const questionLabel = question.label || qId;
              parts.push(`${questionLabel}`);

              for (const [rId, value] of Object.entries(userResponses[qId])) {
                parts.push(
                  `- ${
                    typeof value !== 'boolean'
                      ? formatRawValue(value, '')
                      : responses[rId].label || rId
                  }`,
                );
              }
            }
          });
      }
    });

    return parts.join('\n');
  }, [consultation, orderedResponses, questions, responses, userResponses]);

  useEffect(() => {
    const resultArray: string[][] = [];

    questionnaire.totalQueue.forEach((item, i) => {
      resultArray[i] = [];
      Object.values(item)
        .flat()
        .forEach((key) => {
          if (key && key in userResponses) {
            resultArray[i].push(key);
          }
        });
    });
    setOrderedResponses(resultArray);
  }, [questionnaire.totalQueue, userResponses]);

  const save = useCallback(async (name?: string) => {
    setSave(true);
    // Store the name for use in the save effect
    setScenarioName(name || '');
  }, []);

  const getPageFromSection = (section: string): string => {
    if (sectionsByPage) {
      for (const [key, page] of Object.entries(sectionsByPage)) {
        if (page.sections.includes(section)) {
          return key;
        }
      }
    }
    return '';
  };

  const buildScenarioData = useCallback((): Scenario => {
    return {
      patient: {
        firstName: consultation.patient?.firstName ?? '',
        lastName: consultation.patient?.lastName ?? '',
        dateOfBirth: consultation.patient?.dateOfBirth
          ? new Date(consultation.patient?.dateOfBirth)
          : new Date(),
        sex: consultation.patient?.sex ?? 'MALE',
        self: consultation.patient?.self ?? true,
      },
      consultation: {
        date: consultation.date ? new Date(consultation.date) : new Date(),
      },
      responses: orderedResponses.map((page) =>
        page
          .filter(
            (qId) => !['CONSULTATION_DATE', 'CONSULTATION_TIME'].includes(qId),
          )
          .map((qId) => {
            const section =
              questions[qId].section ?? questionnaire.sectionAssignments[qId];
            return {
              questionId: qId,
              currentPage: getPageFromSection(section),
              currentSection: section,
              responses: userResponses[qId],
            };
          }),
      ),
    };
  }, [
    consultation,
    orderedResponses,
    questions,
    questionnaire.sectionAssignments,
  ]);

  // Build preview scenario when buildPreview is true
  useEffect(() => {
    if (
      buildPreview &&
      orderedResponses.length > 0 &&
      Object.keys(questions).length > 0 &&
      consultation.patient
    ) {
      // Only dispatch if there's an active queue to add
      if (activeQueue && activeQueue.length > 0) {
        dispatch(addToTotalQueue({ currentPage, queue: activeQueue }));
      }

      const scenarioData = buildScenarioData();
      const previewScenario: ScenarioEntity = {
        id: 'preview',
        name: 'Preview Scenario',
        description: generateScenarioDescription(),
        data: scenarioData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      setSavedScenario(previewScenario);
      setScenarioTxt(JSON.stringify(previewScenario, null, 2));
    }
  }, [
    buildPreview,
    buildScenarioData,
    generateScenarioDescription,
    dispatch,
    currentPage,
    activeQueue,
    orderedResponses.length,
    questions,
    consultation.patient,
  ]);

  // Handle actual saving when doSave is triggered
  useEffect(() => {
    if (doSave && !isSaving) {
      const saveScenarioToAPI = async () => {
        setIsSaving(true);
        setError(null);

        try {
          const current = buildScenarioData();

          const response = await scenarioClient.post('/scenario', {
            data: current,
            name: scenarioName,
            description: generateScenarioDescription(),
          });

          setSavedScenario(response.data);
          setScenarioTxt(JSON.stringify(response.data, null, 2));

          // Invalidate scenarios query cache so the loading modal shows latest scenarios
          queryClient.invalidateQueries('scenarios');
        } catch (err) {
          setError(
            err instanceof Error ? err.message : 'Failed to save scenario',
          );
        } finally {
          setIsSaving(false);
          setSave(false); // Reset doSave
        }
      };

      saveScenarioToAPI();
    }
  }, [
    doSave,
    isSaving,
    buildScenarioData,
    generateScenarioDescription,
    scenarioName,
  ]);

  return { save, scenarioTxt, isSaving, error, savedScenario };
};
